import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Drawer,
  Grid,
  makeStyles,
  MenuItem,
  Typography,
} from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import { errorMessages } from 'common/errorMessages.strings';
import { Button, MoreOptionsMenu, SmSpace, TextField } from 'm2m.components';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { RootState } from 'store';
import { RepresentanteLegalInvestidorState, updateRepresentantesLegais } from 'store/modules/investidores/registro';
import { validaCpf } from 'utils/validation';
import { CpfMask } from 'components/masks';
import { addAlert } from 'store/modules/snackBar';

const useStyles = makeStyles(() => ({
  fixedHeight: {
    height: '100px',
  },
  fixedHeightCentered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '155px',
  },
  wrapTxt: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  overflow: {
    overflow: 'hidden',
  },
}));

const mapStateToProps = (state: RootState) => ({
  investidor: state.investidor,
});

const mapDispatchToProps = { updateRepresentantesLegais, addAlert };

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const RepresentantesLegais: React.FC<Props> = (props) => {
  const classes = useStyles();

  const { investidor, updateRepresentantesLegais, addAlert } = props;

  const [openNewRepresentanteDrawer, setOpenNewRepresentanteDrawer] = useState<boolean>(false);
  const [representanteLegalEdit, setRepresentanteLegalEdit] = useState<RepresentanteLegalInvestidorState>();
  const [openDeleteConfirmDrawer, setOpenDeleteConfirmDrawer] = useState<boolean>(false);
  const [nome, setNome] = useState<string>('');
  const [cpf, setCpf] = useState<string>('');

  const handleSubmitClick = () => {
    if (!validaCpf(cpf)) {
      addAlert({ severity: 'error', message: errorMessages.investidor.cpfInvalido });
      return;
    }
    if (investidor.representantesLegais.some((representante) => representante.cpf === cpf)) {
      addAlert({ severity: 'error', message: errorMessages.investidor.cpfDuplicado });
      return;
    }
    const updatedRepresentates = investidor.representantesLegais.concat({
      nome,
      cpf,
    } as RepresentanteLegalInvestidorState);
    updateRepresentantesLegais(updatedRepresentates);
    setOpenNewRepresentanteDrawer(false);
    setNome('');
    setCpf('');
  };

  const handleDeleteClick = (cpf: string) => {
    setRepresentanteLegalEdit(investidor.representantesLegais.find((representante) => representante.cpf === cpf));
    setOpenDeleteConfirmDrawer(true);
  };

  const handleDeleteItem = () => {
    const updatedRepresentates = investidor.representantesLegais.filter(
      (investidor) => investidor.cpf !== representanteLegalEdit?.cpf
    );
    updateRepresentantesLegais(updatedRepresentates);
    setOpenDeleteConfirmDrawer(false);
  };

  return (
    <>
      <Grid container spacing={3} style={{ paddingRight: '12px', width: '100%' }}>
        {investidor.representantesLegais.map((representanteLegal) => (
          <Grid item sm={4} key={representanteLegal.cpf}>
            <Card variant="outlined">
              <CardHeader
                style={{ paddingBottom: 0 }}
                classes={{ content: classes.overflow }}
                action={
                  <MoreOptionsMenu>
                    <MenuItem onClick={() => handleDeleteClick(representanteLegal.cpf)}>
                      <DeleteIcon fontSize="small" style={{ marginRight: '10px' }} />
                      {'Excluir'}
                    </MenuItem>
                  </MoreOptionsMenu>
                }
                title={
                  <Typography variant="h3" className={classes.wrapTxt}>
                    {representanteLegal.nome}
                  </Typography>
                }
              />
              <CardContent className={classes.fixedHeight}>
                <div>
                  <Typography variant="body2">{'CPF:'}</Typography>
                  <Typography variant="body1">{representanteLegal.cpf}</Typography>
                </div>
              </CardContent>
            </Card>
          </Grid>
        ))}
        <Grid item sm={4}>
          <Card variant="outlined">
            <CardContent className={classes.fixedHeightCentered}>
              <Button onClick={() => setOpenNewRepresentanteDrawer(true)} data-testId="btnNovoRepresentante">
                <div>
                  <AddCircleIcon fontSize="small" />
                  <SmSpace />
                  <Typography variant="subtitle1">{'Novo Representante'}</Typography>
                </div>
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Drawer
        anchor="right"
        open={openNewRepresentanteDrawer}
        onClose={() => setOpenNewRepresentanteDrawer(false)}
        PaperProps={{ style: { padding: '40px', width: 465 } }}
      >
        <div>
          <Typography variant="h3">{'Adicionar Representantes Legais'}</Typography>
          <SmSpace />
          <Divider variant="fullWidth" />
          <SmSpace />
          <TextField
            label="Nome"
            value={nome}
            required
            onChange={(value) => setNome(value.replace(/[^À-ÿa-z ]/gi, ''))}
          />
          <SmSpace />
          <TextField label="CPF" value={CpfMask(cpf)} required onChange={setCpf} />
          <SmSpace />
          <Button variant="contained" disabled={!(nome && cpf)} fullWidth color={'primary'} onClick={handleSubmitClick}>
            {'Adicionar Representante'}
          </Button>
          <SmSpace />
        </div>
      </Drawer>
      <Drawer
        anchor="right"
        open={openDeleteConfirmDrawer}
        onClose={() => setOpenDeleteConfirmDrawer(false)}
        PaperProps={{ style: { padding: '40px', width: 465 } }}
      >
        <div>
          <Typography variant="h3">{'Excluir representante'}</Typography>
          <SmSpace />
          <Typography variant="body2">{`Tem certeza que deseja excluir o representante ${representanteLegalEdit?.nome}?`}</Typography>
          <SmSpace />
          <Divider variant="fullWidth" />
          <SmSpace />
          <Button onClick={handleDeleteItem} variant="contained" color="error" fullWidth>
            {'Sim, excluir'}
          </Button>
          <SmSpace />
          <Button onClick={() => setOpenDeleteConfirmDrawer(false)} variant="outlined" fullWidth>
            {'Cancelar'}
          </Button>
        </div>
      </Drawer>
    </>
  );
};
export default connect(mapStateToProps, mapDispatchToProps)(RepresentantesLegais);
